import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../App";
import Watermark from '../components/Watermark';

const BOOKMAKER_LABELS = {
  betway_bw: 'Betway',
  pstbets_bw: 'PST Bets',
  sunbet_bw: 'Sunbet',
  bet267_bw: 'Bet267',
  bbets_bw: 'BBets',
  betxplosion_bw: 'BetXplosion',
};

function bmLabel(key) {
  return BOOKMAKER_LABELS[key] || key;
}

const SPORT_EMOJI = {
  soccer: "⚽", football: "⚽", futsal: "⚽", basketball: "🏀", tennis: "🎾",
  "american-football": "🏈", "ice-hockey": "🏒", baseball: "⚾", cricket: "🏏",
  "rugby-union": "🏉", "rugby-league": "🏉", handball: "🤾", volleyball: "🏐",
  "table-tennis": "🏓", darts: "🎯", snooker: "🎱", boxing: "🥊", mma: "🥊",
};
function sportEmoji(sport = "") {
  return SPORT_EMOJI[(sport || "").toLowerCase()] || "🏅";
}

const SPORT_LABELS = {
  soccer: 'Football', football: 'Football', 'ice-hockey': 'Ice Hockey', 'rugby-union': 'Rugby Union',
  'rugby-league': 'Rugby League', 'american-football': 'American Football', 'table-tennis': 'Table Tennis',
  'water-polo': 'Water Polo',
};

function sportLabel(s = '') {
  return SPORT_LABELS[s] || s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// The scanner stores the pick as home / draw / away. Show the team name instead.
function selectionLabel(b) {
  if (b.selection === "home") return b.homeTeam;
  if (b.selection === "away") return b.awayTeam;
  if (b.selection === "draw") return "Draw";
  return b.selection;
}

function formatKickoff(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return d.toLocaleDateString("en-BW", { weekday: "short", day: "numeric", month: "short" }) + " " +
    d.toLocaleTimeString("en-BW", { hour: "2-digit", minute: "2-digit" });
}

function GradePill({ grade }) {
  const map = { great: ["pill-green", "🔥 GREAT"], good: ["pill-teal", "✅ GOOD"], fair: ["pill-amber", "⚡ FAIR"], weak: ["pill-blue", "WEAK"] };
  const [cls, label] = map[grade] || ["pill-blue", grade];
  return <span className={`pill ${cls}`} style={{ fontSize: 10 }}>{label}</span>;
}

function DetailStat({ label, value, highlight }) {
  return (
    <div style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: "9px 10px" }}>
      <div style={{ fontSize: 10, color: "var(--text3)", marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: highlight ? "var(--accent)" : "var(--text)" }}>{value}</div>
    </div>
  );
}

export default function ValueBets() {
  const [bets, setBets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(() => {
  const league = sessionStorage.getItem('vb_filter_league');
  const bookmaker = sessionStorage.getItem('vb_filter_bookmaker');
  sessionStorage.removeItem('vb_filter_league');
  sessionStorage.removeItem('vb_filter_bookmaker');
  return { sport: "", minEdge: 0, grade: "", market: "", league: league || "", bookmaker: bookmaker || "" };
});
  const [sort, setSort] = useState("edgePct");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selected, setSelected] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isMobile = width < 768;

  const fetchBets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (filters.sport) params.set("sport", filters.sport);
      if (filters.minEdge) params.set("minEdge", filters.minEdge);
      if (filters.grade) params.set("grade", filters.grade);
      if (filters.league) params.set("league", filters.league);
      if (filters.bookmaker) params.set("bookmaker", filters.bookmaker);
      if (filters.market) params.set("market", filters.market);
      const [data, sum] = await Promise.all([
        apiFetch(`/value-bets?${params}`),
        apiFetch("/value-bets/summary"),
      ]);
      setBets(data.bets || []);
      setSummary(sum);
      setLastRefresh(new Date());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchBets(); }, [fetchBets]);
  useEffect(() => {
  const t = setInterval(() => {
    if (!document.hidden) fetchBets();
  }, 120000);
  
  const handleVisibility = () => {
    if (!document.hidden) fetchBets();
  };
  document.addEventListener('visibilitychange', handleVisibility);
  
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', handleVisibility);
  };
}, [fetchBets]);

  const sorted = [...bets].sort((a, b) => {
    if (sort === "edgePct") return b.edgePct - a.edgePct;
    if (sort === "odds") return b.odds - a.odds;
    if (sort === "commenceTime") return new Date(a.commenceTime) - new Date(b.commenceTime);
    return 0;
  });

  // Fix 1 — clicking grade summary card filters by that grade
  const handleSummaryClick = (grade) => {
    setFilters(f => ({ ...f, grade: f.grade === grade ? "" : grade }));
  };

  return (
    <div className="no-select" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Watermark />

      {/* Summary cards — clickable to filter */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2,1fr)" : "repeat(4,1fr)", gap: 10 }}>
          <div
            className="stat-card"
            style={{ "--accent-color": "var(--accent)", cursor: "pointer", border: !filters.grade ? "1px solid var(--accent)" : "1px solid transparent" }}
            onClick={() => handleSummaryClick("")}
          >
            <div className="stat-label">Total Active</div>
            <div className="stat-value">{summary.total}</div>
            <div className="stat-change up">↑ Active now</div>
          </div>
          {summary.byGrade?.map(g => (
            <div
              key={g._id}
              className="stat-card"
              style={{
                "--accent-color": g._id === "great" ? "var(--success)" : g._id === "good" ? "var(--accent)" : "var(--accent3)",
                cursor: "pointer",
                border: filters.grade === g._id ? "1px solid var(--accent)" : "1px solid transparent",
                outline: filters.grade === g._id ? "1px solid rgba(0,229,176,0.3)" : "none",
              }}
              onClick={() => handleSummaryClick(g._id)}
            >
              <div className="stat-label">{g._id?.toUpperCase()}</div>
              <div className="stat-value">{g.count}</div>
              <div className="stat-change up">avg +{g.avgEdge?.toFixed(1)}%</div>
            </div>
          ))}
        </div>
      )}

      {/* Fix 2 — Filters stacked on mobile */}
      <div className="card" style={{ padding: "14px 16px" }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr auto",
          gap: 10,
          alignItems: "end",
        }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Sport</label>
            <select value={filters.sport} onChange={e => setFilters(f => ({ ...f, sport: e.target.value }))} style={{ width: "100%", fontSize: 13 }}>
              <option value="">All My Sports</option>
              {(summary?.bySport || []).map(s => (
                <option key={s._id} value={s._id}>{sportEmoji(s._id)} {sportLabel(s._id)} ({s.count})</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Min Edge</label>
            <select value={filters.minEdge} onChange={e => setFilters(f => ({ ...f, minEdge: parseFloat(e.target.value) }))} style={{ width: "100%", fontSize: 13 }}>
              <option value={0}>Any</option>
              <option value={1}>+1%</option>
              <option value={2}>+2%</option>
              <option value={3}>+3%</option>
              <option value={5}>+5%</option>
            </select>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Grade</label>
            <select value={filters.grade} onChange={e => setFilters(f => ({ ...f, grade: e.target.value }))} style={{ width: "100%", fontSize: 13 }}>
              <option value="">All</option>
              <option value="great">🔥 Great</option>
              <option value="good">✅ Good</option>
              <option value="fair">⚡ Fair</option>
            </select>
          </div>
          <div>
  <label style={{ display: "block", fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Market</label>
  <select value={filters.market} onChange={e => setFilters(f => ({ ...f, market: e.target.value }))} style={{ width: "100%", fontSize: 13 }}>
    <option value="">All Markets</option>
    <option value="1X2">1X2 (3-way)</option>
    <option value="Match Winner">Match Winner</option>
    <option value="Double Chance">Double Chance</option>
    <option value="Over/Under">Over/Under (all lines)</option>
    <option value="BTTS">BTTS</option>
  </select>
</div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "var(--text3)", fontFamily: "var(--font-display)", textTransform: "uppercase", letterSpacing: .5, marginBottom: 6 }}>Sort</label>
            <select value={sort} onChange={e => setSort(e.target.value)} style={{ width: "100%", fontSize: 13 }}>
              <option value="edgePct">Edge %</option>
              <option value="odds">Odds</option>
              <option value="commenceTime">Kick-off</option>
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, gridColumn: isMobile ? "span 2" : "auto" }}>
            {lastRefresh && <span style={{ fontSize: 11, color: "var(--text3)" }}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button className="btn btn-outline" style={{ fontSize: 11, padding: "6px 12px", whiteSpace: "nowrap" }} onClick={fetchBets} disabled={loading}>
              {loading ? "..." : "↺ Refresh"}
            </button>
          </div>
        </div>
      </div>

{(filters.league || filters.bookmaker) && (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
    <span style={{ fontSize: 12, color: 'var(--text3)' }}>Filtered from Heat Map:</span>
    {filters.league && (
      <span className="pill pill-teal" style={{ fontSize: 11, cursor: 'pointer' }}
        onClick={() => setFilters(f => ({ ...f, league: "" }))}>
        🏆 {filters.league} ✕
      </span>
    )}
    {filters.bookmaker && (
      <span className="pill pill-blue" style={{ fontSize: 11, cursor: 'pointer' }}
        onClick={() => setFilters(f => ({ ...f, bookmaker: "" }))}>
        📚 {BOOKMAKER_LABELS[filters.bookmaker] || filters.bookmaker} ✕
      </span>
    )}
  </div>
)}

      {/* Main content */}
      <div style={{ display: "grid", gridTemplateColumns: selected && !isMobile ? "1fr 340px" : "1fr", gap: 16 }}>
        <div className="card">
          {loading ? (
            <div style={{ padding: 20 }}>
              {[...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height: 52, marginBottom: 8 }} />)}
            </div>
          ) : sorted.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🔍</div>
              <p>No value bets match your filters right now.</p>
            </div>
          ) : isMobile ? (
            /* Fix 3 — Mobile card layout for value bets */
            <div>
              {sorted.map(b => (
                <div
                  key={b._id}
                  onClick={() => setSelected(s => s?._id === b._id ? null : b)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--border)',
                    background: selected?._id === b._id ? 'var(--bg3)' : 'transparent',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sportEmoji(b.sport)} {b.homeTeam} vs {b.awayTeam}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                        {selectionLabel(b)} · {bmLabel(b.bookmakeName)} · {b.odds}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--success)', fontSize: 15 }}>+{b.edgePct}%</div>
                      <GradePill grade={b.grade} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop table */
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Match</th>
                  <th>Selection</th>
                  <th>Bookmaker</th>
                  <th>Kick-off</th>
                  <th style={{ textAlign: "right" }}>Odds</th>
                  <th style={{ textAlign: "right" }}>Fair</th>
                  <th style={{ textAlign: "right" }}>Edge</th>
                  <th style={{ textAlign: "right" }}>Grade</th>
                  <th style={{ textAlign: "right" }}>½ Kelly</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(b => (
                  <tr key={b._id} onClick={() => setSelected(s => s?._id === b._id ? null : b)}
                    style={{ background: selected?._id === b._id ? "var(--bg4)" : undefined, cursor: "pointer" }}>
                    <td><div className="sport-badge" style={{ background: "rgba(0,229,176,0.07)", fontSize: 14 }}>{sportEmoji(b.sport)}</div></td>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{b.homeTeam} vs {b.awayTeam}</div>
                      <div style={{ fontSize: 11, color: "var(--text3)" }}>{sportLabel(b.sport)}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{selectionLabel(b)}</div>
                      <div style={{ fontSize: 10, color: "var(--text3)", textTransform: "uppercase", letterSpacing: .5 }}>{b.market}</div>
                    </td>
                    <td><span className="pill pill-blue" style={{ fontSize: 10 }}>{bmLabel(b.bookmakeName)}</span></td>
                    <td style={{ fontSize: 12, color: "var(--text3)" }}>{formatKickoff(b.commenceTime)}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>{b.odds}</td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontSize: 13, color: "var(--text3)" }}>{b.fairOdds}</td>
                    <td style={{ textAlign: "right" }}>
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: b.edgePct >= 5 ? "var(--success)" : "var(--accent3)", fontSize: 14 }}>
                        +{b.edgePct}%
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}><GradePill grade={b.grade} /></td>
                    <td style={{ textAlign: "right", fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--accent)", fontSize: 13 }}>
                      P{b.kellyHalf}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail panel — fullscreen modal on mobile, sidebar on desktop */}
        {selected && (
          isMobile ? (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(8,13,23,0.95)',
              backdropFilter: 'blur(8px)',
              overflow: 'auto',
              padding: '20px 16px',
            }}>
              <div className="card" style={{ maxWidth: 480, margin: '0 auto' }}>
                <div className="section-header">
                  <div className="section-title">Bet Details</div>
                  <button onClick={() => setSelected(null)} style={{ color: "var(--text3)", fontSize: 22, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                </div>
                <BetDetailContent selected={selected} bmLabel={bmLabel} />
              </div>
            </div>
          ) : (
            <div className="card" style={{ height: "fit-content", position: "sticky", top: 80 }}>
              <div className="section-header">
                <div className="section-title">Bet Details</div>
                <button onClick={() => setSelected(null)} style={{ color: "var(--text3)", fontSize: 18, lineHeight: 1, background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
              </div>
              <BetDetailContent selected={selected} bmLabel={bmLabel} />
            </div>
          )
        )}
      </div>
    </div>
  );
}

// Fix 4 — Bet detail uses proper bookmaker labels
function BetDetailContent({ selected, bmLabel }) {
  return (
    <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ fontSize: 20 }}>
        {sportEmoji(selected.sport)}
      </div>
      <div>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 15 }}>
          {selected.homeTeam} vs {selected.awayTeam}
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 3 }}>
          {sportLabel(selected.sport)} · {(() => {
            const d = new Date(selected.commenceTime);
            return d.toLocaleDateString("en-BW", { weekday: "short", day: "numeric", month: "short" }) + " " +
              d.toLocaleTimeString("en-BW", { hour: "2-digit", minute: "2-digit" });
          })()}
        </div>
      </div>

      <div style={{ background: "var(--bg3)", borderRadius: "var(--radius-sm)", padding: 14 }}>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: .5 }}>Your bet</div>
        <div style={{ fontWeight: 600, marginBottom: 3 }}>{selectionLabel(selected)}</div>
        {/* Fix 4 — show proper bookmaker name */}
        <div style={{ fontSize: 12, color: "var(--text3)" }}>{selected.market} · {bmLabel(selected.bookmakeName)}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <DetailStat label="Odds offered" value={selected.odds} highlight />
        <DetailStat label="Fair odds" value={selected.fairOdds} />
        <DetailStat label="Edge" value={`+${selected.edgePct}%`} highlight />
        <DetailStat label="Implied prob" value={`${(selected.impliedProb * 100).toFixed(1)}%`} />
        <DetailStat label="Model prob" value={`${(selected.modelProb * 100).toFixed(1)}%`} />
        <DetailStat label="Grade" value={selected.grade?.toUpperCase()} />
      </div>

      <div style={{ background: "rgba(0,229,176,0.05)", border: "1px solid var(--border2)", borderRadius: "var(--radius-sm)", padding: 14 }}>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 10, textTransform: "uppercase", letterSpacing: .5 }}>Kelly stakes per P100 bankroll</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, textAlign: "center" }}>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--accent)", fontSize: 16 }}>P{selected.kellyFull}</div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>Full Kelly</div>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--accent2)", fontSize: 16 }}>P{selected.kellyHalf}</div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>½ Kelly ✓</div>
          </div>
          <div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--accent3)", fontSize: 16 }}>P{selected.kellyQuarter}</div>
            <div style={{ fontSize: 10, color: "var(--text3)" }}>¼ Kelly</div>
          </div>
        </div>
      </div>

      <button className="btn btn-primary" style={{ width: "100%" }}>
        + Log this bet
      </button>
    </div>
  );
}